import { useEffect, useState } from "react";
import { useAuth } from "@/providers/auth-provider";
import { useSPAContext } from "@/providers/spa-provider";
import { getBoxDetails } from "@/services/box/get-box-details";
import { updateBox } from "@/services/box/update-box";
import {
  GetBoxDetailsOutput,
  PokemonBoxData,
  UpdateBoxInput,
} from "types/web-app";
import { StandardFrame } from "@/components/ui/frames/standard-frame";
import { SimpleButton } from "@/components/ui/buttons/simple-button";
import { RouteNames } from "@/types/route-names";
import { Alert } from "@/components/alert/alert";
import { BackIcon, TrashIcon, PencilIcon } from "@/icons";
import { deleteBox } from "@/services/box/delete-box";
import { BoxEditModal } from "@/components/box/box-edit-modal";

import { PokemonSprite } from "@/components/pokemon/pokemon-sprite";

export const BoxDetailsView = () => {
  const { getAccessToken } = useAuth();
  const { setCurrentRoute } = useSPAContext();
  const [data, setData] = useState<GetBoxDetailsOutput | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const boxId = window.localStorage.getItem("selected_box_id");

  const fetchDetails = async () => {
    if (!boxId) return;
    const token = getAccessToken();
    if (!token) return;

    setIsLoading(true);
    try {
      const response = await getBoxDetails(token, { box_id: boxId });
      if (response.ok) {
        setData(response.data);
      }
    } catch (error) {
      console.error(error);
      Alert("Failed to fetch box details");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!boxId) {
      setCurrentRoute(RouteNames.BOX_LIST);
      return;
    }
    fetchDetails();
  }, [boxId]);

  const handleUpdateBox = async (input: UpdateBoxInput) => {
    const token = getAccessToken();
    if (!token) return;

    try {
      const response = await updateBox(token, input);
      if (response.ok && response.data.box) {
        setData((prev) => (prev ? { ...prev, box: response.data.box } : null));
        Alert("Box updated successfully");
      } else {
        Alert(response.message || "Failed to update box");
      }
    } catch (e) {
      Alert("Failed to update box");
    }
  };

  return (
    <div className="w-full h-full p-4 flex flex-col gap-4">
      <StandardFrame
        variant="gray"
        className="flex flex-col gap-4 h-full"
        style={{
          backgroundColor: data?.box?.color || "#ccc",
        }}
      >
        <div className="flex items-center justify-between border-b border-white/10 pb-2">
          <div className="flex flex-col px-2">
            <h2
              className="text-xl font-bold text-white uppercase tracking-wider"
              style={{
                textShadow: `2px 2px 2px rgba(0, 0, 0, 0.85)`,
              }}
            >
              {data?.box?.name || "Box Details"}
            </h2>
          </div>
          <div className="flex gap-2">
            {data?.box && data.box.id !== "default" && (
              <>
                <SimpleButton
                  onClick={() => setIsEditModalOpen(true)}
                  variant="gray"
                >
                  <PencilIcon width={20} height={20} className="my-2 mx-1" />
                </SimpleButton>
              </>
            )}
          </div>
        </div>

        {/* Pokemon Grid */}
        <div className="flex-1 overflow-y-auto grid grid-cols-4 gap-2 p-1 content-start">
          {isLoading ? (
            <div className="col-span-4 text-center text-white/50">
              Loading...
            </div>
          ) : data?.pokemons.length === 0 ? (
            <div className="col-span-4 text-center text-white/50 py-8">
              This box is empty.
            </div>
          ) : (
            data?.pokemons.map((p) => (
              <div
                key={p.id}
                className="aspect-square bg-black/30 rounded border border-white/10 flex flex-col items-center justify-center relative hover:border-pk-orange-4 cursor-pointer"
                title={p.base_pokemon_name}
              >
                <PokemonSprite pokemon={p} style={{ scale: 1.5 }} showShadow />
                <div className="text-[10px] text-white/70 text-center break-all p-1 absolute bottom-0 w-full bg-black/50">
                  {p.base_pokemon_name}
                </div>
                <span className="absolute top-1 left-1 text-[9px] text-pk-yellow-1 font-bold bg-black/40 px-1 rounded">
                  Lvl {p.level}
                </span>
              </div>
            ))
          )}
        </div>
      </StandardFrame>

      {data?.box && (
        <BoxEditModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          box={data.box}
          onSave={handleUpdateBox}
        />
      )}
    </div>
  );
};
